import React from 'react';
import { LockIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { values, brandStory } from '../../data/company';
import { consultants } from '../../data/consultants';
import { images } from '../../data/images';
import { PageHero } from '../../components/ui/PageHero';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { Reveal } from '../../components/ui/Reveal';
import { Button } from '../../components/ui/Button';
import { CtaBand } from '../../components/ui/CtaBand';
import { ProcessTimeline } from '../../components/process/ProcessTimeline';
import { ConsultantCard } from '../../components/consultants/ConsultantCard';
const philosophy = [{
  title: 'Our recruitment philosophy',
  text: 'Great appointments come from understanding, not volume. We take time to understand an organisation’s strategy and culture before we speak to a single candidate.'
}, {
  title: 'The Corventa approach to talent identification',
  text: 'We define the market deliberately, research it thoroughly and approach people personally — including those who would never respond to an advertisement.'
}, {
  title: 'Relationship-driven recruitment',
  text: 'Many of the leaders we meet today will be clients, referees or candidates tomorrow. We treat every conversation as the start of a long relationship.'
}, {
  title: 'Our commitment to clients and candidates',
  text: 'Clients receive honest advice and transparent progress. Candidates receive respect, clarity and timely feedback — and their details are never shared without permission.'
}];
export function About() {
  usePageMeta('About Us', 'Learn about Corventa Talent Search — our mission, values, recruitment philosophy and research-led approach to executive search.');
  return <>
      <PageHero crumbs={[{
      label: 'About Us'
    }]} kicker="About Corventa" title="Where Exceptional People Meet Extraordinary Opportunities." intro="Corventa Talent Search is a professional recruitment and executive search firm specialising in senior leadership appointments, specialist search and strategic talent solutions." aside={<img src={images.boardroom} alt="An executive boardroom overlooking a city at dusk" className="aspect-[4/3] w-full rounded-xl object-cover" data-id="element-772" data-mp="k81myc6wacit" />} data-id="element-771" data-mp="2bdekwtdpoymj" />

      <section className="bg-ivory py-24" data-id="element-773" data-mp="t9srhdpp8db3">
        <div className="container-page grid gap-12 md:grid-cols-2" data-id="element-774" data-mp="t8rhlckf5wuy">
          <Reveal data-id="element-775" data-mp="y8m6xbojqedw">
            <p className="text-sm font-medium text-gold-700" data-id="element-776" data-mp="y840jm866js9">Our mission</p>
            <p className="mt-4 font-display text-3xl leading-snug text-ink md:text-4xl" data-id="element-777" data-mp="12zuhib1m45sn">
              To connect organisations with leaders and professionals who create lasting value — through a search process defined by rigour,
              discretion and care.
            </p>
          </Reveal>
          <Reveal delay={0.05} data-id="element-778" data-mp="1gz3ul1uiujse">
            <p className="text-sm font-medium text-gold-700" data-id="element-779" data-mp="1h0ewew4ephgv">Our vision</p>
            <p className="mt-4 font-display text-3xl leading-snug text-ink md:text-4xl" data-id="element-780" data-mp="31hxmplnovnvz">
              To be the most trusted partner for leadership appointments in every market we serve.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-781" data-mp="2sgzqhpjhskny">
        <div className="container-page" data-id="element-782" data-mp="2ni6clw0amrn9">
          <SectionHeading kicker="Core values" title="The principles behind every search." data-id="element-783" data-mp="2nj7pm60vdo7m" />
          <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-784" data-mp="3k6m5o22m3j54">
            {values.map(v => {
            const Icon = v.icon;
            return <div key={v.title} className="border-t border-line pt-6" data-id="element-785" data-mp="3bek5m4wgg4vd">
                  <Icon className="h-6 w-6 text-gold-700" aria-hidden="true" data-id="element-786" data-mp="36gr3bmy9vv0d" />
                  <h3 className="mt-4 font-display text-2xl font-semibold text-ink" data-id="element-787" data-mp="36eo9eru01hqd">{v.title}</h3>
                  <p className="mt-2 leading-relaxed text-muted" data-id="element-788" data-mp="7sbs5wikqdkd">{v.text}</p>
                </div>;
          })}
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24" data-id="element-789" data-mp="3io41mwdmgqjf">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-790" data-mp="3noi9oef840hg">
          <div className="lg:col-span-4" data-id="element-791" data-mp="3nmnmsuke5yaq">
            <SectionHeading kicker="How we think" title="An approach built on understanding." data-id="element-792" data-mp="39pimzcb9tvnx" />
          </div>
          <div className="grid gap-10 sm:grid-cols-2 lg:col-span-8" data-id="element-793" data-mp="3ep3ifcr8flvw">
            {philosophy.map(p => <Reveal key={p.title} data-id="element-794" data-mp="69jt2otomnm4">
                <h3 className="font-display text-2xl font-semibold text-ink" data-id="element-795" data-mp="6amj6uj6km0z">{p.title}</h3>
                <p className="mt-3 leading-relaxed text-muted" data-id="element-796" data-mp="abybj7tfvlcv">{p.text}</p>
              </Reveal>)}
          </div>
        </div>
      </section>

      <section className="bg-midnight py-24 text-white" data-id="element-797" data-mp="1gqruaxf9xes">
        <div className="container-page grid gap-10 lg:grid-cols-12 lg:items-center" data-id="element-798" data-mp="1f78uckdt89b">
          <div className="lg:col-span-6" data-id="element-799" data-mp="p51kwrwvmssi">
            <LockIcon className="h-8 w-8 text-gold-300" aria-hidden="true" data-id="element-800" data-mp="tycq6om1j1ib" />
            <h2 className="mt-6 font-display text-4xl font-semibold leading-tight md:text-5xl" data-id="element-801" data-mp="13cmyzbnelpf5">Professional discretion and confidentiality.</h2>
          </div>
          <ul className="space-y-4 text-white/80 lg:col-span-6" data-id="element-802" data-mp="zajk0d1j02p4">
            <li data-id="element-803" data-mp="1d2ppn571yrab">Code-named searches and controlled release of information for sensitive appointments.</li>
            <li data-id="element-804" data-mp="184eg6vko6ism">Candidate details shared only with explicit permission for each opportunity.</li>
            <li data-id="element-805" data-mp="kd0qkxdwl1bc">Non-disclosure arrangements available for clients and candidates where required.</li>
            <li data-id="element-806" data-mp="gd8nv1b6xpdw">Access to search information limited to the consultants directly involved.</li>
          </ul>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-807" data-mp="ud9lm7w5sgp5">
        <div className="container-page" data-id="element-808" data-mp="pcc6632ja0rc">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-809" data-mp="pbam593ji3xh">
            <SectionHeading kicker="Our search methodology" title="A disciplined, ten-stage process." data-id="element-810" data-mp="25pu2txgaubx6" />
            <Button to="/process" variant="outline" arrow className="self-start md:self-auto" data-id="element-811" data-mp="20riphcqspja7">
              Explore the process
            </Button>
          </div>
          <div className="mt-14" data-id="element-812" data-mp="2ctsbrim77zoy">
            <ProcessTimeline variant="compact" data-id="element-813" data-mp="2csqs4pr4a4vm" />
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24" data-id="element-814" data-mp="2hupsdqrk8loc">
        <div className="container-page" data-id="element-815" data-mp="2mshl2n014btl">
          <SectionHeading kicker="Our story" title="Building a more considered search firm." data-id="element-816" data-mp="2mrgb6lur1vdg" />
          <ol className="mt-14 grid gap-10 md:grid-cols-4" data-id="element-817" data-mp="1p5ses0yand1x">
            {brandStory.map(s => <li key={s.title} className="relative border-t-2 border-gold pt-6" data-id="element-818" data-mp="1u4vini2jfd8b">
                <p className="text-sm font-medium text-gold-700" data-id="element-819" data-mp="1z2f0eryg2850">{s.label}</p>
                <h3 className="mt-2 font-display text-2xl font-semibold text-ink" data-id="element-820" data-mp="23tn7hhcaxhf0">{s.title}</h3>
                <p className="mt-2 leading-relaxed text-muted" data-id="element-821" data-mp="16e9a7pomfqxs">{s.text}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-822" data-mp="1bc1dtgymp1ht">
        <div className="container-page" data-id="element-823" data-mp="13tth1wmsdxcl">
          <SectionHeading kicker="Our consultants" title="Meet the team." intro="Profiles are drafts pending approval." data-id="element-824" data-mp="13ss41mm7n0s8" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-825" data-mp="yv9srpkq09h2">
            {consultants.map(c => <ConsultantCard key={c.slug} consultant={c} data-id="element-826" data-mp="txqguwbhw8sa" />)}
          </div>
        </div>
      </section>

      <CtaBand data-id="element-827" data-mp="kuz0qj7ocoub" />
    </>;
}